import { Control } from "../../../flatbuffers/generated/control/control";
import { BankActionInterface } from "../../../interfaces/Bank.interface";
import { MessageInterface } from "../../../interfaces/Message.interface";
import { resolveMessageEnum } from "../actionButtons/actionButtonUtils";

/**
 * Multi-step actions: one configured command dispatched several times, once per
 * floor the operator picks, with each step labelled.
 *
 * VIP is the only one today. It is a normal Control command — the bank's Actions
 * table configures it like any other, and every step sends that SAME command —
 * but running it means three dispatches: where the car starts, where it
 * collects, where it drops off. A single button cannot express that, which is
 * why VIP had a bespoke 565-line panel and a tab of its own.
 *
 * WHY THIS KEYS ON THE MESSAGE RATHER THAN ON A BUTTON TYPE
 *
 * The obvious home is `buttonType`, alongside TOGGLE / HOLDBACK / SWITCH. It
 * cannot go there yet: `button_type` is a Postgres enum (`action_type`) with
 * eight fixed values, mirrored by a FlatBuffer enum and a Go model, so a ninth
 * would be a coordinated change across three repositories before one button
 * could render.
 *
 * Keying on the message is also defensible on its own terms — "VIP takes three
 * floors" is a fact about the command, not about how an operator chose to
 * present it. `sequenceForAction` is the ONLY place that decides, so moving to
 * a persisted button type later changes this function and nothing that calls it.
 *
 * The lookup goes through `resolveMessageEnum`, the same resolver
 * `buildConfiguredControls` uses, and compares against the generated
 * `Control.VIP`. Both matter: an action stores a message ID, not an enum value,
 * and the ordinals in these enums have been renumbered wholesale between schema
 * revisions — a hardcoded 20 would keep compiling and start selecting a
 * different command.
 */

/** One step of a sequenced action. */
export interface SequenceStep {
  /** Stable id, used for React keys and test selectors. */
  id: string;
  /** English default for the step's label. */
  label: string;
  /** i18n key for that label. */
  labelKey: string;
  /** Sentence shown while this step is active, saying what a pick will mean. */
  prompt: string;
  promptKey: string;
}

export interface SequenceSpec {
  /** Stable id for the whole sequence. */
  id: string;
  steps: SequenceStep[];
  /** Shown once every step has been sent. */
  completeText: string;
  completeKey: string;
}

export const VIP_SEQUENCE: SequenceSpec = {
  id: "vip",
  steps: [
    {
      id: "start",
      label: "Start Position",
      labelKey: "monitoringActions.sequence.vip.start",
      prompt: "Select the floor the car should start from.",
      promptKey: "monitoringActions.sequence.vip.startPrompt",
    },
    {
      id: "pickup",
      label: "Pick Up Floor",
      labelKey: "monitoringActions.sequence.vip.pickUp",
      prompt: "Select the floor to collect the passenger from.",
      promptKey: "monitoringActions.sequence.vip.pickUpPrompt",
    },
    {
      id: "dropoff",
      label: "Drop Off Floor",
      labelKey: "monitoringActions.sequence.vip.dropOff",
      prompt: "Select the floor to drop the passenger at.",
      promptKey: "monitoringActions.sequence.vip.dropOffPrompt",
    },
  ],
  completeText: "Sequence sent. Reset to run it again.",
  completeKey: "monitoringActions.sequence.vip.complete",
};

/**
 * The sequence an action runs, or `undefined` for the ordinary single-dispatch
 * rows — which is every action but VIP.
 *
 * Returns `undefined` rather than an empty sequence for the common case so
 * callers branch on presence; empty `steps` would render a sequence UI with
 * nothing in it.
 */
export function sequenceForAction(
  action: Pick<BankActionInterface, "messageId">,
  messages: MessageInterface[],
): SequenceSpec | undefined {
  const enumValue = resolveMessageEnum(messages, action.messageId);
  if (enumValue === null) return undefined;

  return enumValue === Control.VIP ? VIP_SEQUENCE : undefined;
}

/** Whether a row runs as a sequence. Convenience for render-time branching. */
export const actionIsSequenced = (
  action: Pick<BankActionInterface, "messageId">,
  messages: MessageInterface[],
): boolean => sequenceForAction(action, messages) !== undefined;
